import { useEffect, useMemo, useState } from 'react';
import { getDailyCategorySales } from '../api/categorySales';

const PRESETS = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
];

const COLORS = {
  dress: '#E65F94', top: '#38b6ff', bottom: '#6366f1', set: '#25D366', innerwear: '#f59e0b',
  school: '#14b8a6', footwear: '#8b5cf6', accessories: '#f97316', babycare: '#eab308', unmatched: '#d1d5db',
  // use-cases
  'ethnic-wear': '#f97316', 'party-wear': '#E65F94', 'casual-wear': '#25D366',
  'winter-wear': '#38b6ff', 'sleep-wear': '#8b5cf6', 'active-wear': '#eab308', unspecified: '#9ca3af',
};
const colorOf = (c) => COLORS[c] || '#9ca3af';

// Local-date YYYY-MM-DD (toISOString would shift to UTC and slip a day).
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parse = (s) => new Date(`${s}T00:00:00`);
const shift = (s, n) => { const d = parse(s); d.setDate(d.getDate() + n); return iso(d); };
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const cap = (s) => {
  const t = s.replace(/-/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};
const fmtLong = (s) => parse(s).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
const fmtShort = (s) => parse(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

function Delta({ cur, prev }) {
  if (!prev) return cur ? <span className="text-xs text-gray-400">new</span> : null;
  const pct = Math.round(((cur - prev) / prev) * 100);
  if (pct === 0) return <span className="text-xs text-gray-400">±0%</span>;
  return (
    <span className={`text-xs font-medium ${pct > 0 ? 'text-green-600' : 'text-zoddle-pink'}`}>
      {pct > 0 ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
}

export default function CategorySales() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [prev, setPrev] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [groupBy, setGroupBy] = useState('use_case');
  const [hidden, setHidden] = useState([]);
  const [hover, setHover] = useState(null);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    setHover(null);
    setHidden([]);
    // Previous period = the same number of days immediately before this one.
    const n = Math.round((parse(to) - parse(from)) / 86400000) + 1;
    const pTo = shift(from, -1);
    const pFrom = shift(pTo, -(n - 1));
    Promise.all([getDailyCategorySales(from, to, groupBy), getDailyCategorySales(pFrom, pTo, groupBy)])
      .then(([cur, old]) => { if (live) { setData(cur); setPrev(old); } })
      .catch((e) => { if (live) setError(e.response?.data?.detail || 'Failed to load'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [from, to, groupBy]);

  const view = useMemo(() => {
    if (!data) return null;
    const cats = data.categories.filter((c) => !hidden.includes(c));
    const days = data.days.map((d) => ({
      ...d,
      shown: cats.reduce((s, c) => s + (d.by_category[c] || 0), 0),
    }));
    const sum = (list, c) => list.reduce((s, d) => s + (d.by_category[c] || 0), 0);
    const rows = data.categories
      .map((c) => ({ c, cur: sum(data.days, c), old: prev ? sum(prev.days, c) : 0 }))
      .sort((a, b) => b.cur - a.cur);
    const total = days.reduce((s, d) => s + d.shown, 0);
    const prevTotal = prev ? prev.days.reduce((s, d) => s + cats.reduce((x, c) => x + (d.by_category[c] || 0), 0), 0) : 0;
    const best = days.reduce((b, d) => (d.shown > (b?.shown ?? -1) ? d : b), null);
    return { cats, days, rows, total, prevTotal, best, maxDay: Math.max(1, ...days.map((d) => d.shown)), grand: rows.reduce((s, r) => s + r.cur, 0) };
  }, [data, prev, hidden]);

  const toggle = (c) => setHidden((h) => (h.includes(c) ? h.filter((x) => x !== c) : [...h, c]));
  const applyPreset = (n) => { setFrom(daysAgo(n - 1)); setTo(iso(new Date())); };
  const activePreset = PRESETS.find((p) => from === daysAgo(p.days - 1) && to === iso(new Date()));
  const step = view ? Math.ceil(view.days.length / 8) : 1;
  const hovered = view && hover != null ? view.days[hover] : null;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          {PRESETS.map((p) => (
            <button key={p.label} onClick={() => applyPreset(p.days)}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                activePreset?.label === p.label ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)}
                 className="border border-gray-200 rounded-md px-2 py-1 text-gray-700" />
          <span>→</span>
          <input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)}
                 className="border border-gray-200 rounded-md px-2 py-1 text-gray-700" />
        </div>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          {[['use_case', 'Use-case'], ['category', 'Garment type']].map(([k, label]) => (
            <button key={k} onClick={() => setGroupBy(k)}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                groupBy === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>
        {loading && <span className="text-xs text-gray-400">Loading…</span>}
        <span className="text-xs text-gray-400 ml-auto">Change is vs the previous {data?.days.length || ''} days</span>
      </div>

      {error && <div className="text-sm text-zoddle-pink mb-4">{error}</div>}

      {view && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="border border-gray-100 rounded-xl px-4 py-3">
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Pieces sold</div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-2xl font-semibold text-gray-900">{view.total.toLocaleString('en-IN')}</span>
                <Delta cur={view.total} prev={view.prevTotal} />
              </div>
            </div>
            <div className="border border-gray-100 rounded-xl px-4 py-3">
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Per day</div>
              <div className="text-2xl font-semibold text-gray-900 mt-0.5">{(view.total / view.days.length).toFixed(1)}</div>
            </div>
            <div className="border border-gray-100 rounded-xl px-4 py-3">
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Best day</div>
              <div className="text-2xl font-semibold text-gray-900 mt-0.5">
                {view.best && view.best.shown ? `${view.best.shown}` : '—'}
                {view.best && view.best.shown > 0 && <span className="text-sm font-normal text-gray-400 ml-2">{fmtShort(view.best.date)}</span>}
              </div>
            </div>
            <div className="border border-gray-100 rounded-xl px-4 py-3">
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Top category</div>
              <div className="text-2xl font-semibold text-gray-900 mt-0.5">
                {view.rows[0]?.cur ? cap(view.rows[0].c) : '—'}
                {view.rows[0]?.cur > 0 && view.grand > 0 && (
                  <span className="text-sm font-normal text-gray-400 ml-2">{Math.round((view.rows[0].cur / view.grand) * 100)}%</span>
                )}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 border border-gray-100 rounded-xl p-4">
              <div className="h-6 mb-2 text-sm text-gray-500">
                {hovered ? (
                  <>
                    <span className="font-semibold text-gray-900">{fmtLong(hovered.date)}</span>
                    <span className="mx-2">·</span>
                    <span className="font-semibold text-gray-900">{hovered.shown}</span> pieces
                    {view.cats.filter((c) => hovered.by_category[c]).map((c) => (
                      <span key={c} className="ml-3 whitespace-nowrap">
                        <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: colorOf(c) }} />
                        {cap(c)} {hovered.by_category[c]}
                      </span>
                    ))}
                  </>
                ) : 'Pieces sold per day — hover a bar for details'}
              </div>
              <div className="h-56 flex items-end gap-px" onMouseLeave={() => setHover(null)}>
                {view.days.map((d, i) => (
                  <div key={d.date} onMouseEnter={() => setHover(i)}
                       className={`flex-1 h-full flex flex-col-reverse justify-start rounded-sm ${hover === i ? 'bg-gray-100' : ''}`}>
                    {view.cats.map((c) => {
                      const n = d.by_category[c] || 0;
                      return n ? <div key={c} style={{ height: `${(n / view.maxDay) * 100}%`, background: colorOf(c) }} /> : null;
                    })}
                  </div>
                ))}
              </div>
              <div className="flex gap-px mt-1">
                {view.days.map((d, i) => (
                  <div key={d.date} className="flex-1 relative h-4">
                    {i % step === 0 && (
                      <span className="absolute left-1/2 -translate-x-1/2 text-[10px] text-gray-400 whitespace-nowrap">{fmtShort(d.date)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-gray-100 rounded-xl p-4">
              <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-3">{groupBy === 'use_case' ? 'By use-case' : 'By garment type'} · click to hide</div>
              <div className="space-y-3">
                {view.rows.map(({ c, cur, old }) => {
                  const off = hidden.includes(c);
                  return (
                    <button key={c} onClick={() => toggle(c)} className={`w-full text-left ${off ? 'opacity-30' : ''}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 text-gray-800">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: colorOf(c) }} />
                          {cap(c)}
                        </span>
                        <span className="flex items-center gap-2">
                          <Delta cur={cur} prev={old} />
                          <span className="font-semibold tabular-nums text-gray-900 w-8 text-right">{cur}</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full mt-1.5 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${view.grand ? (cur / view.rows[0].cur) * 100 : 0}%`, background: colorOf(c) }} />
                      </div>
                      <div className="text-[11px] text-gray-400 mt-0.5">{view.grand ? Math.round((cur / view.grand) * 100) : 0}% of sales</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <button onClick={() => setShowTable((s) => !s)} className="text-xs font-semibold text-gray-500 hover:text-gray-800 mb-3">
            {showTable ? '▾ Hide' : '▸ Show'} day-by-day table
          </button>
          {showTable && (
            <div className="overflow-x-auto border border-gray-100 rounded-xl">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-gray-400">
                    <th className="text-left font-medium px-4 py-2">Day</th>
                    {view.cats.map((c) => <th key={c} className="font-medium px-3 py-2 text-right">{c}</th>)}
                    <th className="font-medium px-4 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {[...view.days].reverse().map((d) => (
                    <tr key={d.date} className="border-t border-gray-50">
                      <td className="px-4 py-1.5 text-gray-600">{fmtLong(d.date)}</td>
                      {view.cats.map((c) => (
                        <td key={c} className={`px-3 py-1.5 text-right tabular-nums ${d.by_category[c] ? 'text-gray-900' : 'text-gray-300'}`}>{d.by_category[c] || '–'}</td>
                      ))}
                      <td className="px-4 py-1.5 text-right font-semibold tabular-nums">{d.shown}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.categories.includes('unmatched') && (
            <p className="text-xs text-gray-400 mt-3">
              “unmatched” = sold pieces whose barcode isn't in ZOCS inventory (legacy), so their category is unknown.
              {groupBy === 'use_case' && ' A design with several use-cases is counted under its first one.'}
            </p>
          )}
        </>
      )}
    </div>
  );
}
